import { Route, Routes, useNavigate, useParams } from "react-router";
import { useCallback } from "react";

import { getBreadcrumbsBackUrl } from "components/Breadcrumbs/helpers";
import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import NotFoundPage from "components/error/NotFoundPage";
import { Lock } from "components/icons/generated";
import PageLoading from "components/loading/PageLoading";
import MetaInfoList from "components/MetaInfoList";
import MetaInfoListItem from "components/MetaInfoList/Item";
import SpacePath from "components/SpacePath";
import ViewHeader from "components/ViewHeader";
import ViewHeaderNavigation from "components/ViewHeader/Navigation";
import ViewHeaderScrollCollapse from "components/ViewHeader/ScrollCollapse";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import Tooltip from "ds/components/Tooltip";
import Tab from "ds/components/Tab";
import useErrorHandle from "hooks/useErrorHandle";
import useIntentProject from "shared/IntentProject/useIntentProject";
import TimestampMetaInfo from "components/Timestamp/MetaInfo";
import CopyFieldDropdownMenuItem from "components/CopyField/DropdownMenuItem";
import Callout from "ds/components/Callout";
import FeedbackActions from "ds/components/Feedback/Actions";
import Button from "ds/components/Button";
import useUnlockIntentProject from "shared/IntentProject/useUnlockIntentProject";
import useIntentProjectDownloadHcl from "shared/IntentProject/useDownloadHcl";
import IntentProjectStateBadge from "shared/IntentProject/StateBadge";
import { Action } from "types/generated";
import { checkAllowedAction } from "utils/access";

import { INTENT_PROJECTS_SORT_STORAGE_KEY } from "../IntentProjects/Table/constants";
import IntentProjectIntegrations from "./Integrations";
import IntentProjectResources from "./Resources";
import IntentProjectPolicies from "./Policies";
import IntentProjectHistory from "./History";
import { showCreateIntentProjectDrawer } from "../IntentProjects/CreateIntentProjectDrawer";
import { showIntentProjectDeleteConfirmation } from "../IntentProjects/DeleteConfirmation";
import { showIntentProjectCreateStackDrawer } from "./CreateStackDrawer";
import { showIntentProjectDetailsDrawer } from "./DetailsDrawer";
import IntentProjectEnvironment from "./Environment";
import IntentProjectAgreementBanner from "./AgreementBanner";

const IntentProject = () => {
  const navigate = useNavigate();
  const { intentProjectId } = useParams<{ intentProjectId: string }>();

  const { intentProject, loading, error } = useIntentProject(intentProjectId);
  const { unlockIntentProject, loading: unlockLoading } = useUnlockIntentProject();
  const { downloadHcl, loading: downloadHclLoading } = useIntentProjectDownloadHcl();

  const canUpdateIntentProject = checkAllowedAction(intentProject, Action.IntentProjectUpdate);
  const canDeleteIntentProject = checkAllowedAction(intentProject, Action.IntentProjectDelete);
  const canUnlockIntentProject = checkAllowedAction(intentProject, Action.IntentProjectUnlock);

  useBreadcrumbs(
    [
      {
        title: "Intent Projects",
        link: getBreadcrumbsBackUrl("/intent-projects", INTENT_PROJECTS_SORT_STORAGE_KEY),
      },
      {
        title: intentProject?.name || "",
      },
    ],
    [intentProject?.name]
  );

  const handleDeleteClick = useCallback(() => {
    if (intentProject) {
      showIntentProjectDeleteConfirmation({
        id: intentProject.id,
        name: intentProject.name,
      }).then(() => navigate("/intent-projects"));
    }
  }, [intentProject, navigate]);

  const handleEditClick = useCallback(() => {
    if (intentProject) {
      showCreateIntentProjectDrawer({
        project: intentProject,
      });
    }
  }, [intentProject]);

  const handleUnlockClick = useCallback(() => {
    if (intentProject) {
      unlockIntentProject(intentProject.id);
    }
  }, [intentProject, unlockIntentProject]);

  const handleDownloadHcl = useCallback(() => {
    if (intentProject) {
      downloadHcl(intentProject.id);
    }
  }, [downloadHcl, intentProject]);

  const handleOpenDetailsDrawer = useCallback(() => {
    if (intentProject) {
      showIntentProjectDetailsDrawer({ intentProject });
    }
  }, [intentProject]);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !intentProject) {
    return <PageLoading />;
  }

  if (!intentProject) {
    return <NotFoundPage />;
  }

  const intentProjectUrl = `/intent-project/${intentProject.id}`;

  return (
    <>
      <IntentProjectAgreementBanner />
      {intentProject.isLocked && (
        <Callout variant="warning" icon={Lock}>
          This intent project is locked by {intentProject.lockedBy}.
          <FeedbackActions>
            <Tooltip
              on={(props) => (
                <Button
                  {...props}
                  variant="contrast"
                  size="small"
                  onPress={handleUnlockClick}
                  loading={unlockLoading}
                  disabled={!canUnlockIntentProject.result}
                >
                  Unlock
                </Button>
              )}
              active={!!canUnlockIntentProject.message}
            >
              {canUnlockIntentProject.message}
            </Tooltip>
          </FeedbackActions>
        </Callout>
      )}
      <ViewHeader>
        <ViewHeaderWrapper direction="row" justify="between" fullWidth>
          <ViewHeaderWrapper direction="row" align="center">
            <ViewHeaderTitle>{intentProject.name}</ViewHeaderTitle>
            {intentProject.state !== "NONE" && (
              <IntentProjectStateBadge state={intentProject.state} />
            )}
          </ViewHeaderWrapper>

          <ViewHeaderWrapper direction="row" align="end" shrink="0">
            <Button
              variant="secondary"
              onPress={() =>
                showIntentProjectCreateStackDrawer({
                  projectId: intentProject.id,
                  projectName: intentProject.name,
                })
              }
            >
              Create a stack
            </Button>
            <DropdownMenuEllipsis tooltip="Intent Project actions" dotsSize="medium">
              <DropdownMenuItem
                onAction={handleEditClick}
                isDisabled={!canUpdateIntentProject.result}
                tooltip={canUpdateIntentProject.message}
                tooltipPlacement="left"
              >
                Edit
              </DropdownMenuItem>

              <CopyFieldDropdownMenuItem value={intentProject.id} title="Copy Intent Project ID" />

              <DropdownMenuItem onAction={handleOpenDetailsDrawer}>Details</DropdownMenuItem>

              <DropdownMenuItem onAction={handleDownloadHcl} loading={downloadHclLoading}>
                Download HCL file(s)
              </DropdownMenuItem>
              <DropdownMenuItem
                onAction={handleDeleteClick}
                danger
                isDisabled={!canDeleteIntentProject.result}
                tooltip={canDeleteIntentProject.message}
                tooltipPlacement="left"
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuEllipsis>
          </ViewHeaderWrapper>
        </ViewHeaderWrapper>

        <ViewHeaderScrollCollapse>
          <MetaInfoList>
            <SpacePath
              variant="meta"
              spaceId={intentProject.space.id}
              fallbackName={intentProject.space.name}
            />

            {intentProject.createdBy && (
              <MetaInfoListItem>{intentProject.createdBy}</MetaInfoListItem>
            )}

            <TimestampMetaInfo prefix="Created at" timestamp={intentProject.createdAt} />

            {intentProject.lastResourcesUpdatedAt && (
              <TimestampMetaInfo
                prefix="Last resources updated at"
                timestamp={intentProject.lastResourcesUpdatedAt}
              />
            )}
          </MetaInfoList>
        </ViewHeaderScrollCollapse>

        <ViewHeaderNavigation>
          <Tab label="Resources" to={`${intentProjectUrl}`} end />
          <Tab label="History" to={`${intentProjectUrl}/history`} />
          <Tab label="Policies" to={`${intentProjectUrl}/policies`} />
          <Tab label="Integrations" to={`${intentProjectUrl}/integrations`} />
          <Tab label="Environment" to={`${intentProjectUrl}/environment`} />
        </ViewHeaderNavigation>
      </ViewHeader>

      <Routes>
        <Route index element={<IntentProjectResources intentProject={intentProject} />} />
        <Route
          path="integrations"
          element={<IntentProjectIntegrations intentProject={intentProject} />}
        />
        <Route path="policies" element={<IntentProjectPolicies intentProject={intentProject} />} />
        <Route path="history" element={<IntentProjectHistory intentProject={intentProject} />} />
        <Route
          path="environment"
          element={<IntentProjectEnvironment intentProject={intentProject} />}
        />
      </Routes>
    </>
  );
};

export default IntentProject;
